import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Loader2, Search, ShieldCheck, Trash2 } from "lucide-react";
import api from "../lib/apiClient";
import { toast } from "sonner";

const PLANS = ["FREE", "PREMIUM", "PRO"];

export default function AdminUsers() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [role, setRole] = useState("");
  const [page, setPage] = useState(0);

  const load = useCallback(async () => {
    const p = new URLSearchParams({ search, role, page: String(page) });
    try { setData((await api.get(`/admin/users?${p}`)).data); } catch {}
  }, [search, role, page]);
  useEffect(() => { load(); }, [load]);

  const setPlan = async (id, plan) => {
    try { await api.post(`/admin/users/${id}/plan`, { plan }); toast.success("Offre mise à jour"); load(); }
    catch { toast.error("Échec"); }
  };
  const toggleDisabled = async (u) => {
    try { await api.post(`/admin/users/${u.id}/status`, { disabled: !u.disabled }); toast.success(u.disabled ? "Compte réactivé" : "Compte désactivé"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Échec"); }
  };
  const removeUser = async (u) => {
    if (!window.confirm(`Supprimer DÉFINITIVEMENT ${u.email} et toutes ses données ? Cette action est irréversible.`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Compte supprimé"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Échec"); }
  };
  const toggleRole = async (u, r) => {
    const roles = u.roles.includes(r) ? u.roles.filter((x) => x !== r) : [...u.roles, r];
    try { await api.post(`/admin/users/${u.id}/roles`, { roles }); toast.success("Rôles mis à jour"); load(); }
    catch { toast.error("Échec"); }
  };

  return (
    <div className="space-y-5" data-testid="admin-users">
      <h1 className="font-display text-3xl font-extrabold">Utilisateurs</h1>
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={search} onChange={(e) => { setPage(0); setSearch(e.target.value); }} placeholder="Rechercher email ou nom…"
            data-testid="admin-users-search" className="w-full h-11 pl-9 pr-3 bg-[#0F0F13] border border-zinc-800 rounded-xl text-sm text-zinc-200 outline-none focus:border-amber-500/50" />
        </div>
        {["", "CREATOR", "MANAGER", "PROFESSIONAL", "ORGANIZATION", "ADMIN"].map((r) => (
          <button key={r || "all"} onClick={() => { setPage(0); setRole(r); }} data-testid={`admin-filter-${r || "all"}`}
            className={`px-3 h-11 rounded-xl text-sm border ${role === r ? "bg-amber-500/15 text-amber-400 border-amber-500/40" : "border-zinc-800 text-zinc-400"}`}>{r || "Tous"}</button>
        ))}
      </div>

      {!data ? <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 text-amber-500 animate-spin" /></div> : (
        <>
          <p className="text-xs text-zinc-500">{data.total} utilisateur(s)</p>
          <div className="space-y-2">
            {data.users.map((u) => (
              <div key={u.id} className="bg-[#18181E] border border-zinc-800 rounded-2xl p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid={`admin-user-${u.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold truncate flex items-center gap-2">{u.artist_name || "—"} {u.is_admin && <ShieldCheck className="w-4 h-4 text-amber-400" />}</p>
                  <p className="text-xs text-zinc-500 truncate">{u.email} · niv.{u.level} · {u.total_xp} XP · {(u.roles || []).join("+")}</p>
                </div>
                <select value={u.plan} onChange={(e) => setPlan(u.id, e.target.value)} data-testid={`admin-plan-${u.id}`}
                  className="h-9 bg-[#0F0F13] border border-zinc-800 rounded-lg text-sm px-2 text-zinc-200">
                  {PLANS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
                <button onClick={() => toggleRole(u, "MANAGER")} data-testid={`admin-role-mgr-${u.id}`}
                  className={`h-9 px-3 rounded-lg text-xs border ${u.roles.includes("MANAGER") ? "border-emerald-500/40 text-emerald-400" : "border-zinc-700 text-zinc-500"}`}>Manager</button>
                <button onClick={() => toggleRole(u, "ADMIN")} data-testid={`admin-role-admin-${u.id}`}
                  className={`h-9 px-3 rounded-lg text-xs border ${u.roles.includes("ADMIN") ? "border-amber-500/40 text-amber-400" : "border-zinc-700 text-zinc-500"}`}>Admin</button>
                <Link to={`/admin/users/${u.id}`} data-testid={`admin-view-${u.id}`}
                  className="h-9 px-3 rounded-lg text-xs font-medium bg-zinc-900 border border-zinc-700 text-zinc-300 hover:text-amber-400 hover:border-amber-500/40 inline-flex items-center">Voir</Link>
                <button onClick={() => toggleDisabled(u)} data-testid={`admin-disable-${u.id}`}
                  className={`h-9 px-3 rounded-lg text-xs font-medium ${u.disabled ? "bg-red-500/15 text-red-400 border border-red-500/30" : "bg-zinc-900 border border-zinc-700 text-zinc-300"}`}>{u.disabled ? "Désactivé" : "Actif"}</button>
                {!u.is_admin && (
                  <button onClick={() => removeUser(u)} data-testid={`admin-delete-${u.id}`} title="Supprimer définitivement"
                    className="h-9 px-3 rounded-lg text-xs font-medium bg-zinc-900 border border-zinc-700 text-zinc-400 hover:text-red-400 hover:border-red-500/40 inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /></button>
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between pt-2">
            <button disabled={page === 0} onClick={() => setPage((p) => p - 1)} className="h-9 px-4 rounded-lg text-sm bg-zinc-900 border border-zinc-800 disabled:opacity-40">Précédent</button>
            <button disabled={!data.has_more} onClick={() => setPage((p) => p + 1)} data-testid="admin-users-next" className="h-9 px-4 rounded-lg text-sm bg-zinc-900 border border-zinc-800 disabled:opacity-40">Suivant</button>
          </div>
        </>
      )}
    </div>
  );
}
